import { useRouter } from 'next/router';
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PATHS } from 'src/common/constants';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';

// Pages without nav when nav update flag is enabled
const PAGES_WITHOUT_NAV: string[] = [
  PATHS.ADMIN_ADDONS,
  PATHS.ADMIN_LISTINGS,
  PATHS.ADMIN_LISTING_EDITOR,
];

type OnClickAction = () => void;
type AdminPagesContext = {
  backLabel: string;
  backAria: string;
  backHref?: string;
  backOnClick?: OnClickAction;
  setBackAction: (action: string | OnClickAction) => void;
  setBackLabelHidden: (value: boolean) => void;
  isBackLabelHidden?: boolean;
  backLoading?: boolean;
  setBackLoading: (value: boolean) => void;
  /**
   * Controls whether the navigation bar should be rendered.
   * This is determined by feature flags and the current page path.
   */
  shouldRenderNav: boolean;
};

const adminPagesContext = createContext<AdminPagesContext>({} as AdminPagesContext);

type ProviderProps = {
  backLabel?: string;
  backAria?: string;
  backHref?: string;
  children: React.ReactNode;
};

const AdminPagesProvider: FC<ProviderProps> = ({
  backLabel,
  backAria,
  backHref: href,
  children,
}) => {
  const [backHref, setBackHref] = useState(href);
  const [backOnClick, setBackOnClick] = useState<OnClickAction>();
  const { l: isLargeScreen } = useMediaQueries();
  const [isBackLabelHidden, setBackLabelHidden] = useState(!isLargeScreen);
  const [backLoading, setBackLoading] = useState(false);
  const setBackAction = useCallback((action: string | OnClickAction) => {
    if (typeof action === 'string') {
      setBackHref(action);
      setBackOnClick(undefined);
    } else {
      // curry to prevent auto-running
      setBackOnClick(() => action);
      setBackHref(undefined);
    }
  }, []);

  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const { asPath } = useRouter();
  const shouldRenderNav = !isNavUpdateEnabled || !PAGES_WITHOUT_NAV.some(
    (path) => asPath.startsWith(path),
  );

  useEffect(() => {
    setBackLabelHidden(!isLargeScreen);
  }, [isLargeScreen]);

  const contextValue = useMemo(() => (
    {
      backHref,
      backOnClick,
      setBackAction,
      backLabel,
      backAria,
      isBackLabelHidden,
      setBackLabelHidden,
      setBackLoading,
      backLoading,
      shouldRenderNav,
    }
  ), [
    backAria,
    backHref,
    backLabel,
    backLoading,
    backOnClick,
    isBackLabelHidden,
    setBackAction,
    shouldRenderNav,
  ]);

  return (
    <adminPagesContext.Provider value={contextValue}>
      {children}
    </adminPagesContext.Provider>
  );
};

export default AdminPagesProvider;

export const useAdminPagesContext = (): AdminPagesContext => {
  const context = useContext(adminPagesContext);

  if (!context) {
    throw new Error('useAdminPagesContext must be used within a AdminPagesProvider');
  }

  return context;
};
